import { setupListeners } from '@reduxjs/toolkit/query';
import { ConfigCatProvider, DataGovernance, PollingMode } from 'configcat-react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { config } from './config.ts';
import { isDev } from './configuration/env.ts';
import { main } from './configuration/index.ts';
import { I18NProvider } from './configuration/lang/I18NProvider.tsx';
import { handleSilentLoginRedirect, isLoginRedirect } from './configuration/login/redirect.ts';
import { appRouter } from './routes/Router.tsx';
import { store } from './store/store.ts';

import '@rio-cloud/iframe-resizer/child';
import './configuration/datadog/setup.ts';

// Needed for RTK Query lifecycle/focus features
setupListeners(store.dispatch);

const renderApplication = () => {
    // biome-ignore lint/style/noNonNullAssertion: always there
    return createRoot(document.getElementById('root')!).render(
        <Provider store={store}>
            <I18NProvider>
                <ConfigCatProvider
                    sdkKey={config.configCat.apiKey}
                    pollingMode={PollingMode.AutoPoll}
                    options={{
                        cacheTimeToLiveSeconds: config.configCat.cacheTimeInSeconds,
                        dataGovernance: DataGovernance.EuOnly,
                    }}
                >
                    <RouterProvider router={appRouter} />
                </ConfigCatProvider>
            </I18NProvider>
        </Provider>
    );
};

async function enableMocking() {
    if (!isDev) {
        return;
    }

    const { worker } = await import('./mocks/browser.ts');

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    return worker.start({
        serviceWorker: { url: 'mockServiceWorker.js' },
        onUnhandledRequest: 'bypass',
    });
}

const loginRedirect = isLoginRedirect();
if (loginRedirect && window.parent !== window) {
    handleSilentLoginRedirect();
} else {
    enableMocking().then(() => {
        void main(renderApplication, loginRedirect);
    });
}
